import type { CandidateTimeline } from "@shared/labs/level-assessment-candidate";
import type {
  InterviewPanelResponse,
  KnownRead,
} from "@shared/labs/level-assessment-interview-panel";
import { ArrowUpRight } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "wouter";
import { rpcClient } from "@/lib/rpc";
import { ConfChip, LevelChip } from "../../results/chips";
import { C, DM, UI } from "../../results/ui-tokens";
import { Skeleton } from "../candidate-ui";
import type { ClarifyCard } from "../panel/derive";
import { derivePanelActs } from "../panel/derive";
import { ClarifySection, ResolvedSection } from "../panel/sections";
import { SectionEyebrow } from "../panel/ui";
import { axisLabelEntries } from "./derive";

/** Pinned-model axis scopes — the only thing the open cards can't tell us
 *  themselves (a blind spot knows its gap type, not whether the axis is cross
 *  or vertical). Fetched once; drives the Transversais / Da disciplina split. */
interface PinnedModelAxes {
  model: {
    crossAxes: { id: string; label: string }[];
    disciplines: { id: string; axes: { id: string; label: string }[] }[];
  };
}

/**
 * Visão geral — the case-status lens: what we know, what's still open, and how
 * the next interview clarifies it. A candidate-level recomposition of the
 * LATEST round's briefing (same synthesis, same derivation — never re-scores):
 * established reads as compact rows, then the open points with their probes,
 * then the settled history.
 */

const POLL_MS = 2500;

/** Two-column established grid, collapsing to one column on a narrow viewport.
 *  Self-contained (the candidate page injects no CSS of its own). */
const OVERVIEW_CSS = `
.la-two-col { grid-template-columns: repeat(2, minmax(0, 1fr)); }
@media (max-width: 720px) { .la-two-col { grid-template-columns: 1fr; } }`;

export function OverviewLens({ data }: { data: CandidateTimeline }) {
  const lastRound = data.rounds.at(-1) ?? null;
  const [res, setRes] = useState<InterviewPanelResponse | null>(null);
  const [scopeByAxis, setScopeByAxis] = useState<Map<string, "cross" | "vertical">>(new Map());
  const [error, setError] = useState<string | null>(null);

  const candidateId = data.candidate.id;
  const modelId = data.candidate.modelId;
  const roundId = lastRound?.id ?? null;

  // The axis→scope map is stable per model — fetch once (best-effort; the split
  // simply falls back to a single ungrouped list if it never lands).
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const r = (await rpcClient.labs.levelAssessmentModelGet({ modelId })) as PinnedModelAxes;
        if (cancelled) return;
        const map = new Map<string, "cross" | "vertical">();
        for (const a of axisLabelEntries(r.model)) map.set(a.id, a.scope);
        setScopeByAxis(map);
      } catch {
        // non-fatal — see fallback in the render
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [modelId]);

  useEffect(() => {
    if (!roundId) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const load = async () => {
      try {
        const r = (await rpcClient.labs.levelAssessmentInterviewPanelGet({
          candidateId,
          roundId,
        })) as InterviewPanelResponse;
        if (cancelled) return;
        setRes(r);
        if (r.status === "generating") timer = setTimeout(() => void load(), POLL_MS);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Erro ao montar a visão geral.");
      }
    };
    void load();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [candidateId, roundId]);

  const panel = res?.status === "ready" ? res.panel : null;
  const acts = useMemo(() => (panel ? derivePanelActs(panel) : null), [panel]);

  if (!lastRound) {
    return (
      <div style={{ ...UI, fontSize: 13.5, color: C.ink50, marginTop: 20 }}>Sem rodadas ainda.</div>
    );
  }
  if (error || res?.status === "error") {
    return (
      <div style={{ ...UI, fontSize: 13.5, color: C.ink50, marginTop: 20 }}>
        Falha ao montar a visão geral: {error ?? (res?.status === "error" ? res.error : "")}
      </div>
    );
  }
  if (!panel || !acts) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 14 }}>
        <Skeleton style={{ height: 90, borderRadius: 14 }} />
        <Skeleton style={{ height: 90, borderRadius: 14 }} />
      </div>
    );
  }

  const { cross, vertical } = panel.known;
  const hasEstablished = cross.length > 0 || vertical.length > 0;
  // The current scored level came from the latest round that produced an
  // evaluation — the same "Avaliação Completa" report the timeline links to.
  const reportEvaluationId = data.currentEvaluation?.evaluationId ?? null;

  return (
    <div style={{ marginTop: 6 }}>
      {/** biome-ignore lint/security/noDangerouslySetInnerHtml: static CSS, no user data */}
      <style dangerouslySetInnerHTML={{ __html: OVERVIEW_CSS }} />
      {hasEstablished && (
        <section style={{ marginTop: 16 }}>
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <SectionEyebrow>Competências estabelecidas</SectionEyebrow>
            {reportEvaluationId && (
              <Link
                href={`/labs/level-assessment/e/${reportEvaluationId}`}
                style={{
                  ...UI,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  fontSize: 13,
                  fontWeight: 600,
                  color: C.red,
                  textDecoration: "none",
                }}
              >
                Avaliação geral <ArrowUpRight size={14} />
              </Link>
            )}
          </div>
          <EstablishedGroup label="Transversais" reads={cross} />
          <EstablishedGroup label="Da disciplina" reads={vertical} />
        </section>
      )}

      <section style={{ marginTop: 30 }}>
        <SectionEyebrow>O que falta descobrir</SectionEyebrow>
        <OpenGroups cards={acts.open} scopeByAxis={scopeByAxis} />
      </section>

      {acts.resolved.length > 0 && (
        <section style={{ marginTop: 30 }}>
          <SectionEyebrow>Pontos encerrados</SectionEyebrow>
          <ResolvedSection items={acts.resolved} />
        </section>
      )}
    </div>
  );
}

/** The open competencies, split into Transversais / Da disciplina so a long
 *  list stops reading as one endless column. Preserves the global impact rank
 *  within each group. Falls back to a single ungrouped list until the scope
 *  map loads (or for a cross-only model where the vertical group is empty). */
function OpenGroups({
  cards,
  scopeByAxis,
}: {
  cards: ClarifyCard[];
  scopeByAxis: Map<string, "cross" | "vertical">;
}) {
  if (cards.length === 0) {
    return (
      <div style={{ ...UI, fontSize: 13.5, color: C.ink50, marginTop: 20 }}>
        Nenhum ponto em aberto.
      </div>
    );
  }
  const cross = cards.filter((c) => scopeByAxis.get(c.axisId) === "cross");
  const vertical = cards.filter((c) => scopeByAxis.get(c.axisId) === "vertical");
  // Anything the model doesn't classify (scope map absent, or a synthetic
  // "Perfil geral" axis) — keep it visible rather than drop it.
  const other = cards.filter((c) => !scopeByAxis.has(c.axisId));

  // Not enough scope information to split meaningfully → one dense list.
  if (cross.length === 0 || vertical.length === 0) {
    return (
      <div style={{ marginTop: 4 }}>
        <ClarifySection cards={cards} dense />
      </div>
    );
  }
  return (
    <>
      <OpenGroup label="Transversais" cards={cross} />
      <OpenGroup label="Da disciplina" cards={vertical} />
      {other.length > 0 && <OpenGroup label="Perfil geral" cards={other} />}
    </>
  );
}

/** One labeled scope block of open competencies (dense). */
function OpenGroup({ label, cards }: { label: string; cards: ClarifyCard[] }) {
  if (cards.length === 0) return null;
  return (
    <div style={{ marginTop: 16 }}>
      <div
        style={{
          ...UI,
          fontSize: 11.5,
          fontWeight: 600,
          letterSpacing: "0",
          textTransform: "uppercase",
          color: C.ink50,
          marginBottom: 10,
        }}
      >
        {label}
      </div>
      <ClarifySection cards={cards} dense />
    </div>
  );
}

/** One competency scope (cross / vertical) as a labeled two-column grid of
 *  cells. Omitted when the scope has no established reads. */
function EstablishedGroup({ label, reads }: { label: string; reads: KnownRead[] }) {
  if (reads.length === 0) return null;
  return (
    <div style={{ marginTop: 16 }}>
      <div
        style={{
          ...UI,
          fontSize: 11.5,
          fontWeight: 600,
          letterSpacing: "0",
          textTransform: "uppercase",
          color: C.ink50,
          marginBottom: 10,
        }}
      >
        {label}
      </div>
      <div className="la-two-col" style={{ display: "grid", gap: 12 }}>
        {reads.map((read) => (
          <EstablishedCell key={read.axisId} read={read} />
        ))}
      </div>
    </div>
  );
}

/** One established read as a self-contained cell: level chip + axis + the
 *  explainer's HR-facing summary sentence (a reader with no context on the
 *  interviews must understand it — the telegraphic signal title is only the
 *  fallback), confidence at the right. Depth lives on the round's briefing. */
function EstablishedCell({ read }: { read: KnownRead }) {
  const signal = read.decisiveSignals[0];
  const teaser =
    read.summary ||
    (signal
      ? `${signal.title}${signal.value ? ` · ${signal.value}` : ""}`
      : read.indirect
        ? "Sinal indireto · a confirmar em entrevista"
        : "");
  return (
    <div
      className="avoid-break"
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
        background: C.white,
        border: `1px solid ${C.border}`,
        borderRadius: 14,
        padding: "14px 16px",
      }}
    >
      <LevelChip level={read.level === null ? "—" : `L${read.level}`} />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ ...DM, display: "block", fontSize: 14.5, fontWeight: 700, color: C.ink }}>
          {read.axisLabel}
        </span>
        {teaser && (
          <span
            style={{
              ...UI,
              display: "-webkit-box",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 2,
              fontSize: 12.5,
              color: C.ink50,
              lineHeight: 1.45,
              marginTop: 3,
              overflow: "hidden",
            }}
          >
            {teaser}
          </span>
        )}
      </span>
      <ConfChip value={read.confidence} small />
    </div>
  );
}
